import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import {
  apiClient,
  apiUrl,
  builderBotTelegramUsername,
  currentPeriodEnd,
  currentPeriodStart,
  daysUntilReset,
  formatPeriodDate,
  freePostTrialRuns,
  freeTierCap,
  imageGenerationAgentRunMultiplier,
  inFirstMonth,
  isLlmBillingActive,
  nextPeriodResetDate,
  stripePortalPath,
  supergreenCapacityPath,
  thirtyDaysMs,
  tierByName,
} from "../../constants.ts";
import {
  ArrowRightIcon,
  Button,
  CollapsibleCard,
  CopyableValue,
  customConfirm,
  inputBaseClass,
  PageSpinner,
  Spinner,
  TokenDisplay,
} from "./components.tsx";
import { showWidget } from "./chatWidget.tsx";
import {
  ApiTokensSection,
  Prompt2botClientDescription,
} from "./apiTokensSection.tsx";
import { AffiliateSection } from "./talkToSkill.tsx";
import { transact, tx, useAuth, useInfiniteQuery, useQuery } from "./db.ts";
import { usePageMeta } from "./pageMeta.ts";

const periodUsage = (
  count: number | undefined,
  billingPeriodStart: number | undefined,
) => (billingPeriodStart ?? 0) === currentPeriodStart() ? (count ?? 0) : 0;

const agentRunsUsed = (
  agentRunCount: number | undefined,
  llmRunCount: number | undefined,
  imageGenerationCount: number | undefined,
  billingPeriodStart: number | undefined,
) =>
  periodUsage(
    isLlmBillingActive() ? llmRunCount : agentRunCount,
    billingPeriodStart,
  ) +
  periodUsage(imageGenerationCount, billingPeriodStart) *
    imageGenerationAgentRunMultiplier;

const totalTokenCount = (
  promptTokenCount: number | undefined,
  candidatesTokenCount: number | undefined,
  thoughtsTokenCount: number | undefined,
) =>
  (promptTokenCount ?? 0) + (candidatesTokenCount ?? 0) +
  (thoughtsTokenCount ?? 0);

const effectiveTierCap = (
  tier: { name: string; agentRunsPerMonth: number },
  user: {
    phone?: string | null;
    email?: string | null;
    signupTimestamp?: number | null;
  },
) => tier.name === "Free" ? freeTierCap(user) : tier.agentRunsPerMonth;

const UsageLine = ({ label, value }: { label: string; value: number }) => (
  <div className="flex justify-between text-sm text-text-secondary-light dark:text-text-secondary">
    <span>{label}</span>
    <span>{value.toLocaleString()}</span>
  </div>
);

const usageBarTrackStyle: Record<string, string> = {
  width: "100%",
  height: "8px",
  borderRadius: "4px",
  overflow: "hidden",
};

const usageBarFillStyle = (
  ratio: number,
): Record<string, string> => ({
  height: "100%",
  borderRadius: "4px",
  width: `${Math.min(ratio * 100, 100)}%`,
  transition: "width 0.3s ease",
});

const usageBarColor = (ratio: number) =>
  ratio >= 0.9 ? "bg-danger" : ratio >= 0.7 ? "bg-warning" : "bg-accent";

const UsageBar = (
  { used, limit }: { used: number; limit: number },
) => {
  const ratio = limit > 0 ? used / limit : 1;
  const remaining = Math.max(0, limit - used);
  return (
    <div className="mt-4">
      <div className="flex justify-between text-sm text-text-secondary-light dark:text-text-secondary mb-1">
        <span>
          {used.toLocaleString()} / {limit.toLocaleString()} LLM runs ·{" "}
          <span className="font-medium text-text-primary-light dark:text-text-primary">
            {remaining.toLocaleString()} left
          </span>
        </span>
        <span>{Math.round(ratio * 100)}%</span>
      </div>
      <div
        className="bg-border-subtle-light dark:bg-border-subtle"
        style={usageBarTrackStyle}
      >
        <div
          className={usageBarColor(ratio)}
          style={usageBarFillStyle(ratio)}
        />
      </div>
    </div>
  );
};

const updateNewsletterSubscription =
  (userId: string) => (subscribed: boolean) =>
    toast.promise(
      transact(
        tx.$users[userId].update({ unsubscribeFromNewsletter: !subscribed }),
      ),
      {
        loading: "Updating...",
        success: subscribed
          ? "Subscribed to newsletter"
          : "Unsubscribed from newsletter",
        error: "Failed to update subscription",
      },
    );

const redirectToStripePortal = (customerId: string) =>
  fetch(`${apiUrl}/${stripePortalPath}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customerId }),
  })
    .then((res) => res.json())
    .then(({ url }: { url: string }) => {
      globalThis.location.href = url;
    });

const openStripePortal = (customerId: string | undefined) => {
  if (!customerId) return;
  toast.promise(redirectToStripePortal(customerId), {
    loading: "Opening billing portal...",
    success: "Redirecting...",
    error: "Failed to open billing portal",
  });
};

const BillingSection = (
  { customerId }: { customerId: string | undefined },
) => (
  <div className="mt-6 pt-6 border-t border-border-subtle-light dark:border-border-subtle">
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="font-medium">Payment method</p>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary">
          Changed cards? Update your credit card here. You can also view
          invoices or cancel — all handled securely by Stripe.
        </p>
      </div>
      <Button onClick={() => openStripePortal(customerId)}>
        Update card
      </Button>
    </div>
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        openStripePortal(customerId);
      }}
      className="group inline-flex items-center gap-1.5 mt-3 text-sm text-accent-light dark:text-accent hover:opacity-80"
    >
      <span>View invoices or cancel subscription</span>
      <ArrowRightIcon className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
    </a>
  </div>
);

const PhoneLinkSection = (
  { currentPhone, token }: { currentPhone: string | undefined; token: string },
) => {
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [pinSent, setPinSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleRequestPin = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error(
        "Enter a valid phone number with country code (at least 10 digits)",
      );
      return;
    }
    setSending(true);
    try {
      const result = await apiClient({
        endpoint: "request-phone-link",
        token,
        payload: { phone: digits },
      });
      if (result.success) {
        setPinSent(true);
        toast.success("PIN sent via SMS");
      } else {
        toast.error(
          result.error === "this-phone-belongs-to-another-web-account"
            ? "This phone is already linked to another account"
            : result.error === "invalid-phone"
            ? "Invalid phone number. Please include your country code (e.g. +972 for Israel) and omit the leading 0."
            : result.error === "too-many-otp-attempts-for-this-number"
            ? "Too many verification attempts for this phone number. Please try again in 24 hours."
            : result.error === "too-many-otp-attempts-from-this-ip"
            ? "Too many verification attempts from your IP address. Please try again later."
            : result.error ?? "Failed to send PIN",
        );
      }
    } catch (e) {
      toast.error("Failed to send PIN");
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const handleVerifyPin = async () => {
    const digits = phone.replace(/\D/g, "");
    setVerifying(true);
    try {
      const result = await apiClient({
        endpoint: "verify-phone-link",
        token,
        payload: { phone: digits, pin },
      });
      if (result.success) {
        toast.success("Phone linked successfully!");
        setPinSent(false);
        setPhone("");
        setPin("");
      } else {
        toast.error(
          result.error === "invalid-pin"
            ? "Invalid PIN"
            : result.error ?? "Verification failed",
        );
      }
    } catch (e) {
      toast.error("Verification failed");
      console.error(e);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <CollapsibleCard
      title="Phone Link"
      subtitle={currentPhone ? `Linked: ${currentPhone}` : "Not linked"}
    >
      {currentPhone
        ? (
          <p className="text-sm text-text-secondary-light dark:text-text-secondary">
            Linked phone: <strong>{currentPhone}</strong>
          </p>
        )
        : (
          <>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary mb-3">
              Link your phone number to unify your web and mobile accounts.
            </p>
            {!pinSent
              ? (
                <div className="flex items-center gap-2">
                  <input
                    type="tel"
                    placeholder="e.g. 972501234567"
                    value={phone}
                    onChange={(e) => setPhone(e.currentTarget.value)}
                    className={`flex-1 max-w-md ${inputBaseClass}`}
                  />
                  <Button
                    disabled={sending || !phone.replace(/\D/g, "")}
                    onClick={handleRequestPin}
                  >
                    {sending ? "Sending..." : "Send PIN"}
                  </Button>
                </div>
              )
              : (
                <div>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary mb-2">
                    Enter the 6-digit PIN sent to {phone} via SMS:
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      maxLength={6}
                      placeholder="123456"
                      value={pin}
                      onChange={(e) => setPin(e.currentTarget.value)}
                      className={`w-32 ${inputBaseClass} text-center tracking-widest`}
                    />
                    <Button
                      disabled={verifying || pin.length !== 6}
                      onClick={handleVerifyPin}
                    >
                      {verifying ? "Verifying..." : "Verify"}
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        setPinSent(false);
                        setPin("");
                      }}
                      className="text-sm text-text-tertiary-light dark:text-text-tertiary hover:text-text-primary-light dark:hover:text-text-primary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
          </>
        )}
    </CollapsibleCard>
  );
};

const TelegramLinkSection = (
  { currentTelegramId, token }: {
    currentTelegramId: string | undefined;
    token: string;
  },
) => {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleRequestLink = async () => {
    setLoading(true);
    try {
      const result = await apiClient({
        endpoint: "request-telegram-link",
        token,
        payload: {},
      });
      if (result.success && result.pin) {
        setPin(result.pin);
        toast.success(
          "Code generated! Send it to the builder bot on Telegram.",
        );
      } else {
        toast.error(result.error ?? "Failed to generate linking code");
      }
    } catch (e) {
      toast.error("Failed to generate linking code");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !confirm("Are you sure you want to disconnect your Telegram account?")
    ) return;
    setDisconnecting(true);
    try {
      const result = await apiClient({
        endpoint: "disconnect-telegram-link",
        token,
        payload: {},
      });
      if (result.success) {
        toast.success("Telegram account disconnected successfully!");
        setPin("");
      } else {
        toast.error(result.error ?? "Failed to disconnect Telegram account");
      }
    } catch (e) {
      toast.error("Failed to disconnect Telegram account");
      console.error(e);
    } finally {
      setDisconnecting(false);
    }
  };

  const botLink = pin
    ? `https://t.me/${builderBotTelegramUsername}?start=${pin}`
    : "";

  return (
    <CollapsibleCard
      title="Telegram Link"
      subtitle={currentTelegramId
        ? `Linked ID: ${currentTelegramId}`
        : "Not linked"}
    >
      {currentTelegramId
        ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary">
                Linked Telegram ID: <strong>{currentTelegramId}</strong>
              </p>
              <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
                Your Telegram account is unified with your web account. You will
                be recognized as an administrator on your Telegram bots
                automatically.
              </p>
            </div>
            <Button
              className="bg-danger hover:bg-danger-hover border-none text-white"
              disabled={disconnecting}
              onClick={handleDisconnect}
            >
              {disconnecting ? "Disconnecting..." : "Disconnect"}
            </Button>
          </div>
        )
        : (
          <>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary mb-3">
              Link your Telegram account to recognize your administrator
              permissions automatically across all your Telegram bots.
            </p>
            {!pin
              ? (
                <Button disabled={loading} onClick={handleRequestLink}>
                  {loading ? "Generating..." : "Link Telegram Account"}
                </Button>
              )
              : (
                <div className="flex flex-col gap-4 max-w-md">
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary">
                    Send this code to the builder bot on Telegram, or simply
                    click the button below to link instantly:
                  </p>
                  <CopyableValue
                    label="Linking Code"
                    value={pin}
                  />
                  <div className="flex gap-2 items-center mt-2">
                    <Button
                      onClick={() => {
                        globalThis.open(botLink, "_blank");
                      }}
                      className="bg-accent hover:bg-accent-hover text-white border-none"
                    >
                      Open Bot & Link Instantly
                    </Button>
                    <button
                      type="button"
                      onClick={() => setPin("")}
                      className="text-sm text-text-tertiary-light dark:text-text-tertiary hover:text-text-primary-light dark:hover:text-text-primary ml-2"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
          </>
        )}
    </CollapsibleCard>
  );
};

const settingsBotPageSize = 25;

const dedupeAndSortBots = (
  adminBots: {
    id: string;
    name: string;
    agentRunCount?: number;
    llmRunCount?: number;
    imageGenerationCount?: number;
    incomingMessageCount?: number;
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    thoughtsTokenCount?: number;
  }[],
  billedBots: {
    id: string;
    name: string;
    agentRunCount?: number;
    llmRunCount?: number;
    imageGenerationCount?: number;
    incomingMessageCount?: number;
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    thoughtsTokenCount?: number;
  }[],
) =>
  Object.values(
    [...billedBots, ...adminBots].reduce(
      (acc, bot) => ({ ...acc, [bot.id]: acc[bot.id] ?? bot }),
      {} as Record<string, typeof adminBots[number]>,
    ),
  ).sort(
    (a, b) =>
      ((b.agentRunCount ?? 0) +
          (b.imageGenerationCount ?? 0) * imageGenerationAgentRunMultiplier) -
        ((a.agentRunCount ?? 0) +
          (a.imageGenerationCount ?? 0) * imageGenerationAgentRunMultiplier) ||
      (b.incomingMessageCount ?? 0) - (a.incomingMessageCount ?? 0),
  );

const AliceAndBotUserIdentitySection = ({
  userId,
  currentPublicSignKey,
  credentials,
}: {
  userId: string;
  currentPublicSignKey: string | undefined;
  credentials: unknown;
}) => {
  const [credentialsInput, setCredentialsInput] = useState("");

  return (
    <CollapsibleCard
      title="Alice & Bot Identity"
      subtitle={currentPublicSignKey
        ? `Public Key: ${currentPublicSignKey.slice(0, 10)}...`
        : undefined}
    >
      <p className="text-sm text-text-secondary-light dark:text-text-secondary mb-3">
        Your account uses a public identity via its publicSignKey. Replace your
        identity below.
      </p>
      {currentPublicSignKey && (
        <div className="mb-4">
          <label className="block text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary mb-1">
            Current Public Sign Key
          </label>
          <CopyableValue value={currentPublicSignKey} />
        </div>
      )}
      {credentials && (
        <div className="mb-4">
          <label className="block text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary mb-1">
            Secret Credentials
          </label>
          <p className="text-xs text-text-secondary-light dark:text-text-secondary mb-2">
            This credentials string gives access to all your conversations on
            this network. Keep it secure and do not share it.
          </p>
          <TokenDisplay
            token={JSON.stringify(credentials)}
            label="Credentials"
          />
        </div>
      )}
      <div className="pt-2 border-t border-border-subtle-light dark:border-border-subtle">
        <div className="text-sm font-medium mb-1">Replace Identity</div>
        <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mb-2">
          Paste a secret credentials string to replace your user Alice & Bot
          identity in the database.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={credentialsInput}
            onInput={({ currentTarget: { value } }) => {
              setCredentialsInput(value);
            }}
            placeholder="Paste secret credentials JSON string here"
            className={`${inputBaseClass} flex-1`}
          />
          <Button
            onClick={async () => {
              const trimmed = credentialsInput.trim();
              if (!trimmed) return;
              try {
                const creds = JSON.parse(trimmed);
                if (!creds.privateSignKey || !creds.publicSignKey) {
                  throw new Error("Invalid credentials format: missing keys");
                }
                const confirmed = await customConfirm(
                  "Replacing your identity will cause you to lose access to all your current conversations forever. Are you sure you want to proceed?",
                  {
                    title: "Warning: Permanent Loss of Conversations",
                    confirmText: "Replace Identity",
                    cancelText: "Cancel",
                  },
                );
                if (!confirmed) return;

                const op = () => {
                  return transact(
                    tx.$users[userId].update({
                      aliceAndBotCredentials: creds,
                    }),
                  ).then(() => {
                    setCredentialsInput("");
                    showWidget.value = false;
                    return "Identity replaced successfully";
                  });
                };
                toast.promise(op(), {
                  loading: "Replacing identity...",
                  success: (msg: string) => msg,
                  error: (e: Error) =>
                    e.message || "Failed to replace identity",
                });
              } catch (err) {
                toast.error(
                  err instanceof Error ? err.message : "Invalid JSON format",
                );
              }
            }}
            disabled={!credentialsInput.trim()}
          >
            Replace
          </Button>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export const Settings = () => {
  usePageMeta({
    title: "Settings | prompt2bot",
    description: "Manage your prompt2bot account, billing, and API tokens.",
  });
  const { user } = useAuth();
  const token = user?.refresh_token;
  const { data } = useQuery(
    user?.id
      ? {
        $users: {
          $: { where: { id: user.id } },
        },
      }
      : null,
  );
  const {
    data: adminBotsData,
    canLoadNextPage: canLoadMoreAdminBots,
    loadNextPage: loadMoreAdminBots,
  } = useInfiniteQuery(
    user?.id
      ? {
        bots: {
          $: {
            where: { admins: user.id },
            limit: settingsBotPageSize,
            order: { serverCreatedAt: "desc" as const },
          },
        },
      }
      : null,
  );
  const {
    data: billedBotsData,
    canLoadNextPage: canLoadMoreBilledBots,
    loadNextPage: loadMoreBilledBots,
  } = useInfiniteQuery(
    user?.id
      ? {
        bots: {
          $: {
            where: { billingUser: user.id },
            limit: settingsBotPageSize,
            order: { serverCreatedAt: "desc" as const },
          },
        },
      }
      : null,
  );
  const currentUser = data?.$users?.[0];
  const canLoadMoreBots = canLoadMoreAdminBots || canLoadMoreBilledBots;
  const loadMoreBots = () => {
    if (canLoadMoreAdminBots) loadMoreAdminBots();
    if (canLoadMoreBilledBots) loadMoreBilledBots();
  };
  const allBots = dedupeAndSortBots(
    adminBotsData?.bots ?? [],
    billedBotsData?.bots ?? [],
  );
  const [supergreenCapacity, setSupergreenCapacity] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (!token) return;
    fetch(`${apiUrl}/${supergreenCapacityPath}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((r) => setSupergreenCapacity(r.paidCapacity ?? 0))
      .catch(() => setSupergreenCapacity(0));
  }, [token]);

  if (!currentUser) {
    return (
      <div className="container mx-auto px-4 py-8 flex flex-col flex-1">
        <h1 className="text-3xl font-display font-bold mb-6">Settings</h1>
        <PageSpinner />
      </div>
    );
  }

  const isSubscribed = !currentUser.unsubscribeFromNewsletter;
  const tier = tierByName(currentUser.tier ?? "");
  const tierCap = effectiveTierCap(tier, currentUser);
  const runsUsed = agentRunsUsed(
    currentUser.agentRunCount,
    currentUser.llmRunCount,
    currentUser.imageGenerationCount,
    currentUser.billingPeriodStart,
  );
  const runsRemaining = Math.max(0, tierCap - runsUsed);
  const isFree = tier.name === "Free";
  const inTrial = inFirstMonth(currentUser.signupTimestamp);
  const trialEndsDate = currentUser.signupTimestamp
    ? formatPeriodDate(currentUser.signupTimestamp + thirtyDaysMs)
    : undefined;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-display font-bold mb-6">Settings</h1>

      <PhoneLinkSection
        currentPhone={currentUser.phone}
        token={token ?? ""}
      />

      <TelegramLinkSection
        currentTelegramId={currentUser.telegramId}
        token={token ?? ""}
      />

      <AliceAndBotUserIdentitySection
        userId={currentUser.id}
        currentPublicSignKey={currentUser.aliceAndBotCredentials?.publicSignKey}
        credentials={currentUser.aliceAndBotCredentials}
      />

      <ApiTokensSection
        mode="user"
        title="API Tokens"
        description={<Prompt2botClientDescription />}
        token={token ?? ""}
        createButtonLabel="Create token"
        createPlaceholder="Token name (e.g. ci-deploy)"
        collapsible
      />

      <CollapsibleCard
        title="Subscription"
        subtitle={`${tier.emoji} ${tier.name} Tier · ${runsUsed.toLocaleString()} / ${tierCap.toLocaleString()} LLM runs (${runsRemaining.toLocaleString()} left)`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">
              {tier.emoji} {tier.name} Tier
            </p>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary">
              {tierCap.toLocaleString()} LLM runs/month{isFree
                ? currentUser.phone
                  ? " (phone verified)"
                  : user?.email
                  ? " — verify phone for higher limit"
                  : " — add an email or verify phone for higher limit"
                : ""}
            </p>
            {isFree && (
              <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
                {inTrial
                  ? `Free trial active until ${trialEndsDate} · drops to ${freePostTrialRuns} runs/month after trial`
                  : `Free trial ended · ${freePostTrialRuns} runs/month on free tier. Upgrade to continue running bots.`}
              </p>
            )}
          </div>
          {!currentUser.stripeSubscriptionId && (
            <a
              href="/pricing"
              className="px-4 py-2 rounded-lg font-semibold text-white bg-accent hover:bg-accent-hover transition-colors text-sm"
            >
              Upgrade
            </a>
          )}
        </div>
        <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-3">
          Billing period: {formatPeriodDate(currentPeriodStart())} –{" "}
          {formatPeriodDate(currentPeriodEnd())} · Resets on{" "}
          <strong className="font-semibold text-text-secondary-light dark:text-text-secondary">
            {formatPeriodDate(nextPeriodResetDate())}
          </strong>{" "}
          ({daysUntilReset()} {daysUntilReset() === 1 ? "day" : "days"} left)
        </p>
        <UsageBar
          used={runsUsed}
          limit={tierCap}
        />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
          <UsageLine
            label="Runs remaining this period"
            value={runsRemaining}
          />
          <UsageLine
            label="Agent runs this period"
            value={periodUsage(
              currentUser.agentRunCount,
              currentUser.billingPeriodStart,
            )}
          />
          <UsageLine
            label="LLM runs this period"
            value={periodUsage(
              currentUser.llmRunCount,
              currentUser.billingPeriodStart,
            )}
          />
          <UsageLine
            label="Image generations this period"
            value={periodUsage(
              currentUser.imageGenerationCount,
              currentUser.billingPeriodStart,
            )}
          />
          <UsageLine
            label="Image run-equivalent"
            value={periodUsage(
              currentUser.imageGenerationCount,
              currentUser.billingPeriodStart,
            ) * imageGenerationAgentRunMultiplier}
          />
          <UsageLine
            label="Prompt tokens this period"
            value={periodUsage(
              currentUser.promptTokenCount,
              currentUser.billingPeriodStart,
            )}
          />
          <UsageLine
            label="Response tokens this period"
            value={periodUsage(
              currentUser.candidatesTokenCount,
              currentUser.billingPeriodStart,
            )}
          />
          <UsageLine
            label="Thought tokens this period"
            value={periodUsage(
              currentUser.thoughtsTokenCount,
              currentUser.billingPeriodStart,
            )}
          />
          <UsageLine
            label="Total tokens this period"
            value={totalTokenCount(
              periodUsage(
                currentUser.promptTokenCount,
                currentUser.billingPeriodStart,
              ),
              periodUsage(
                currentUser.candidatesTokenCount,
                currentUser.billingPeriodStart,
              ),
              periodUsage(
                currentUser.thoughtsTokenCount,
                currentUser.billingPeriodStart,
              ),
            )}
          />
        </div>
        <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-3">
          How quotas work: Monthly LLM run quotas are shared across all your
          bots and the AI builder, resetting on the 1st of each month (UTC).
          Each image generation counts as {imageGenerationAgentRunMultiplier}
          {" "}
          LLM runs.
        </p>
        {allBots.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-2">
              Per-bot usage
            </p>
            <div className="border border-border-subtle-light dark:border-border-subtle rounded-lg divide-y divide-border-subtle-light dark:divide-border-subtle">
              {allBots.map((bot) => (
                <div
                  key={bot.id}
                  className="flex items-center justify-between px-4 py-2 text-sm"
                >
                  <span className="truncate mr-4">{bot.name}</span>
                  <span className="text-text-tertiary-light dark:text-text-tertiary whitespace-nowrap">
                    {(bot.agentRunCount ?? 0).toLocaleString()} runs ·{" "}
                    {(bot.llmRunCount ?? 0).toLocaleString()} LLM runs ·{" "}
                    {(bot.imageGenerationCount ?? 0).toLocaleString()} images ·
                    {" "}
                    {totalTokenCount(
                      bot.promptTokenCount,
                      bot.candidatesTokenCount,
                      bot.thoughtsTokenCount,
                    ).toLocaleString()} tokens ·{" "}
                    {(bot.incomingMessageCount ?? 0).toLocaleString()} msgs
                  </span>
                </div>
              ))}
            </div>
            {canLoadMoreBots && (
              <button
                type="button"
                onClick={loadMoreBots}
                className="mt-2 text-sm text-accent-light dark:text-accent hover:opacity-80"
              >
                Load more bots...
              </button>
            )}
            <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-2">
              Per-bot counts are approximate and may not reflect the exact
              billing period boundary.
            </p>
          </div>
        )}
        {currentUser.stripeSubscriptionId && currentUser.stripeCustomerId && (
          <BillingSection customerId={currentUser.stripeCustomerId} />
        )}
      </CollapsibleCard>

      {supergreenCapacity === null
        ? (
          <CollapsibleCard
            title="Supergreen WhatsApp Lines"
            subtitle="Loading..."
          >
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          </CollapsibleCard>
        )
        : supergreenCapacity > 0
        ? (
          <CollapsibleCard
            title="Supergreen WhatsApp Lines"
            subtitle={`${supergreenCapacity} paid ${
              supergreenCapacity === 1 ? "line" : "lines"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {supergreenCapacity} paid{" "}
                  {supergreenCapacity === 1 ? "line" : "lines"}
                </p>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary">
                  ${supergreenCapacity * 10}/month ($10/line)
                </p>
              </div>
            </div>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary mt-4 pt-4 border-t border-border-subtle-light dark:border-border-subtle">
              WhatsApp lines are billed separately. To update the card for these
              lines, view invoices, or cancel, log in at{" "}
              <a
                href="https://supergreen.cc"
                target="_blank"
                rel="noreferrer"
                className="text-accent-light dark:text-accent hover:opacity-80"
              >
                supergreen.cc
              </a>{" "}
              with this email.
            </p>
          </CollapsibleCard>
        )
        : null}

      <AffiliateSection
        activeUser={currentUser}
        token={token}
      />

      <CollapsibleCard
        title="Newsletter Preferences"
        subtitle={isSubscribed ? "Subscribed" : "Unsubscribed"}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Newsletter Subscription</p>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary">
              Receive updates and news about prompt2bot
            </p>
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isSubscribed}
              onChange={(e) =>
                updateNewsletterSubscription(currentUser.id)(
                  (e.target as HTMLInputElement).checked,
                )}
            />
            <div className="w-11 h-6 bg-border-subtle-light peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/30 rounded-full peer dark:bg-border-subtle peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-border-default-light after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-border-default peer-checked:bg-accent">
            </div>
          </label>
        </div>
      </CollapsibleCard>
    </div>
  );
};
